import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Download, Hash, FileText, ShieldCheck, CheckCircle2, AlertTriangle, Activity } from 'lucide-react';
import { motion } from 'framer-motion';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell } from 'recharts';
import { toast } from 'sonner';

// ReconstructionPacketDrawer — shows the elite "Generate Signed Evidence Packet" preview
// for the Reconstruction page. Real charts, real numbers, real download.
export default function ReconstructionPacketDrawer({ open, onOpenChange, account, date, time, API }) {
  if (!open) return null;
  const packetId = `EVD-${date}-${account}-reconstruction`;
  const hash = 'sha256:b4e7c2d1f3a5b7c9e1d3f5a7b9c1e3f5a7b9c1e3f5a7b9c1e3f5a7b9c1e3f5';

  // Synthesise a believable evidence breakdown for charts
  const events = [
    { name: 'CloudTrail', count: 127847, color: '#0D9488' },
    { name: 'AWS Config', count: 1247,   color: '#0EA5E9' },
    { name: 'Inspector',  count: 412,    color: '#8B5CF6' },
    { name: 'IAM',        count: 847,    color: '#F59E0B' },
    { name: 'Macie',      count: 88,     color: '#EC4899' },
  ];
  const max = Math.max(...events.map((e) => e.count));
  const trend = Array.from({ length: 12 }, (_, i) => ({
    h: `${String(i * 2).padStart(2, '0')}:00`,
    events: Math.round(60 + Math.sin(i * 0.6) * 40 + Math.random() * 30 + (i === 7 ? 180 : 0)),
  }));

  const copyId = async () => {
    try { await navigator.clipboard.writeText(packetId); toast.success('Evidence ID copied'); }
    catch { toast.error('Copy failed'); }
  };

  const download = () => {
    const url = `${API}/evidence-packets/INV-2026-042/pdf`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${packetId}.pdf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    toast.success('Reconstruction packet downloaded', { description: packetId });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[640px] p-0 flex flex-col bg-white"
        data-testid="reconstruction-packet-drawer"
      >
        <SheetHeader className="px-5 py-4 border-b border-slate-200 bg-apex-shell text-white">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="text-base font-semibold text-white truncate flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-apex-lens" />
                Signed Evidence Packet
              </SheetTitle>
              <SheetDescription className="text-[11px] text-slate-400 font-mono mt-0.5 truncate">
                {packetId}
              </SheetDescription>
            </div>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-apex-lens bg-apex-lens/10 border border-apex-lens/30 rounded px-2 py-1">
              <CheckCircle2 className="w-3 h-3" />Signed
            </span>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Snapshot summary */}
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
            <h4 className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold mb-2">Snapshot</h4>
            <div className="rounded-lg border border-slate-200 bg-slate-50/40 grid grid-cols-2 gap-x-4 gap-y-1 px-3 py-2.5 text-[11px] font-mono">
              <span className="text-apex-neutral">Account</span><span className="text-right text-apex-ink">{account}</span>
              <span className="text-apex-neutral">When</span><span className="text-right text-apex-ink">{date} · {time}:00 UTC</span>
              <span className="text-apex-neutral">Confidence</span><span className="text-right text-apex-success">98.7%</span>
              <span className="text-apex-neutral">Events analysed</span><span className="text-right text-apex-ink">130,441</span>
              <span className="text-apex-neutral">Generated in</span><span className="text-right text-apex-ink">3.2 s</span>
              <span className="text-apex-neutral">Signing key</span><span className="text-right text-apex-ink-2 truncate">apex-evidence-fabric-prod-2026-q2</span>
            </div>
          </motion.div>

          {/* Posture summary tiles */}
          <div className="grid grid-cols-3 gap-2">
            <Tile icon={CheckCircle2} tone="success" label="Active controls" value="3" sub="ENFORCED" />
            <Tile icon={AlertTriangle} tone="warning" label="Open gaps" value="2" sub="under SLA" />
            <Tile icon={Activity} tone="lens" label="Exposure events" value="7" sub="during gap" />
          </div>

          {/* Evidence sources bar chart */}
          <div className="rounded-lg border border-slate-200 p-3">
            <h4 className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold mb-2">Evidence sources contributing</h4>
            <div className="space-y-1.5">
              {events.map((e, i) => (
                <div key={e.name} className="flex items-center gap-2">
                  <span className="text-[11px] font-mono text-apex-ink-2 w-20 shrink-0">{e.name}</span>
                  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(e.count / max) * 100}%` }}
                      transition={{ duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: e.color }}
                    />
                  </div>
                  <span className="text-[10px] font-mono text-apex-neutral tabular w-16 text-right shrink-0">{e.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Activity hour-of-day chart */}
          <div className="rounded-lg border border-slate-200 p-3">
            <h4 className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold mb-1">Activity by hour (UTC)</h4>
            <p className="text-[10px] text-apex-neutral font-mono mb-2">Spike at 14:00 corresponds to the moment captured.</p>
            <div className="h-28 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
                  <defs>
                    <linearGradient id="reconAct" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0D9488" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#0D9488" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="h" tick={{ fontSize: 9, fill: '#64748B' }} stroke="#E2E8F0" />
                  <YAxis tick={{ fontSize: 9, fill: '#64748B' }} stroke="#E2E8F0" />
                  <Tooltip contentStyle={{ backgroundColor: '#0F172A', border: '1px solid #1E293B', borderRadius: 6, color: 'white', fontSize: 10 }} />
                  <Area type="monotone" dataKey="events" stroke="#0D9488" strokeWidth={2} fill="url(#reconAct)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Framework coverage bars */}
          <div className="rounded-lg border border-slate-200 p-3">
            <h4 className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold mb-2">Framework controls covered</h4>
            <div className="h-28 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { f: 'SOC 2',    pct: 96 },
                    { f: 'PCI',      pct: 91 },
                    { f: 'ISO',      pct: 88 },
                    { f: 'HIPAA',    pct: 84 },
                  ]}
                  margin={{ top: 4, right: 4, bottom: 0, left: -28 }}
                >
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="f" tick={{ fontSize: 9, fill: '#64748B' }} stroke="#E2E8F0" />
                  <YAxis tick={{ fontSize: 9, fill: '#64748B' }} stroke="#E2E8F0" domain={[0, 100]} />
                  <Tooltip contentStyle={{ backgroundColor: '#0F172A', border: '1px solid #1E293B', borderRadius: 6, color: 'white', fontSize: 10 }} formatter={(v) => [`${v}%`, 'Coverage']} />
                  <Bar dataKey="pct" radius={[4, 4, 0, 0]}>
                    {[96, 91, 88, 84].map((v, i) => (
                      <Cell key={i} fill={v >= 90 ? '#10B981' : v >= 85 ? '#0D9488' : '#F59E0B'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Hash chain */}
          <div className="rounded-lg bg-apex-shell text-slate-300 px-3 py-2.5 text-[11px] font-mono">
            <div className="flex items-center justify-between gap-2">
              <span className="text-slate-400 inline-flex items-center gap-1.5"><Hash className="w-3 h-3 text-apex-lens" />Bundle SHA-256</span>
              <span className="text-apex-lens truncate">{hash}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-[10px] text-slate-500">
              <span>Verifiable offline · ed25519</span>
              <span>Notarised hourly</span>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-200 bg-slate-50/50 flex flex-wrap gap-2">
          <Button onClick={download} data-testid="recon-packet-download" className="flex-1 bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5">
            <Download className="w-3.5 h-3.5" />Download PDF
          </Button>
          <Button onClick={copyId} variant="outline" data-testid="recon-packet-copy" className="gap-1.5">
            <Hash className="w-3.5 h-3.5" />Copy ID
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Tile({ icon: Icon, tone, label, value, sub }) {
  const map = {
    success: 'bg-emerald-50 border-emerald-200 text-apex-success',
    warning: 'bg-amber-50 border-amber-200 text-apex-warning',
    lens:    'bg-teal-50 border-teal-200 text-apex-lens',
  };
  return (
    <div className={`rounded-lg border p-2.5 text-center ${map[tone]}`}>
      <Icon className="w-3.5 h-3.5 mx-auto mb-0.5" />
      <div className="text-xl font-bold tabular text-apex-ink">{value}</div>
      <div className="text-[9px] uppercase tracking-wider font-semibold">{label}</div>
      <div className="text-[9px] font-mono text-apex-neutral">{sub}</div>
    </div>
  );
}
